import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import classnames from 'classnames';
import { t } from 'services/translation';

import { GridContainer, ToggleButton, RevealContainer } from './styles';

interface MessageGuideProps {
  guideName: string;
  showHint?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  borderless?: boolean;
}

export const MessageGuide = ({
  guideName,
  showHint = false,
  value = '',
  onChange,
  placeholder,
  borderless = false,
}: MessageGuideProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [messageValue, setMessageValue] = useState(value);

  useEffect(() => {
    setMessageValue(value);
  }, [value]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setMessageValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const defaultPlaceholder = showHint
    ? t('ENTER_YOUR_MESSAGE_HERE')
    : t('HAVE_SPECIAL_REQUEST');

  const containerClasses = classnames(
    'MessageGuideContainer',
    'CheckoutSection',
    {
      'no-top-border': borderless,
    }
  );

  return (
    <div className={containerClasses}>
      <div
        className="CheckoutMessageGuide"
        data-testid="checkout-message"
        id="checkout-message"
      >
        <GridContainer>
          <ToggleButton
            as="span"
            role="button"
            aria-label="Expands send a message"
            aria-expanded={isExpanded}
            onClick={handleToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
              }
            }}
            tabIndex={0}
            className={`icon anyfont-icon af-promo-${isExpanded ? 'minus' : 'plus'}`}
          />
          <h2>
            {t('SEND_A_MESSAGE_TO')} {guideName} ({t('OPTIONAL')})
          </h2>
        </GridContainer>

        {showHint && (
          <div className="CheckoutMessageGuide-hint">
            <div className="CheckoutMessageGuide-hint-icon">
              <i className="af-lamp brand-color" />
            </div>
            <div className="CheckoutMessageGuide-hint-text">
              {t('REQUESTING_A_PICKUP')}
            </div>
          </div>
        )}

        <RevealContainer isExpanded={isExpanded}>
          <textarea
            className="textarea ui"
            value={messageValue}
            onChange={handleChange}
            placeholder={placeholder || defaultPlaceholder}
            aria-label="Send a message"
            aria-labelledby="checkout-message"
          />
        </RevealContainer>
      </div>
    </div>
  );
};
